import React, { FC, useState, useEffect } from "react";
import {
  styled,
  Container,
  Box,
  Typography,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { useAuth0Token, getHelp } from "../common/http-requests";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { pageMt } from "../common/util";
import { helpRequestType, /* helpResponseType */ } from "../common/type";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { Toolbar } from "../components/FooterToolbar";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { themeColors } from "../common/colors";
import { useLanguage } from "../hooks/useStoreGetters";

interface HelpContent {
  question_text: string;
  answer_text: string;
};

interface HelpCategory {
  category_name: string;
  help_contents: HelpContent[];
};

const LINE_CHART_SIZE: number[] = [
  800, 320
];

const Title = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${LINE_CHART_SIZE[0]}px`,
  margin: 'auto',
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
  textAlign: 'left',
  marginBottom: '2rem',
}));

const Accordion = styled(Box)(() => ({
  overflow: 'hidden',
  transition: 'max-height 0.3s ease-out',
}));

const HelpPage: FC = () => {
  const { t } = useTranslation();
  const { isDesktop } = useCustomMediaQuery();
  const language = useLanguage();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const [helpData, setHelpData] = useState<HelpCategory[]>([]);

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });
  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getHelpData();
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);
  const titleIcon = [
    <HomeOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
    <HistoryEduOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
    <SchoolOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
    <QueryStatsOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
    <TimelineOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
    <BarChartOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
    <EditNoteOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
    <SummarizeOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
    <SmartToyOutlinedIcon sx={{ margin: '.5rem .8rem' }} />,
  ]

  const getHelpData = async () => {
    setIsSending(true);
    try {
      const requestParams: helpRequestType = {
        language,
      };
      const data /*: helpResponseType */ = await getHelp(
        await getToken(),
        requestParams
      );
      setHelpData(data);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  const handleAccordion = (panel: string) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [panel]: !prevExpanded[panel],
    }));
  };
  return (
    <>
      <Container maxWidth="xl">
        {isSending ? (
          <>
            <CircularProgressMolecule displayLabel={t("msg.loading")} />
          </>
        ) : (
          <>
            <VisualizedMenus />
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            ></FeedbackBar>
            <Box mt={pageMt(isDesktop)} className="mb-2 p-2 w-[80%] mx-auto" maxWidth={`${LINE_CHART_SIZE[0]}px`}>
              <Title>
                <Typography
                  variant="h5"
                  style={{ marginTop: 10, textAlign: "left" }}
                >
                  <HelpOutlineOutlinedIcon sx={{ margin: '.5rem .8rem' }} />{t("menu.help")}
                </Typography>
              </Title>
              {helpData.map((category, categoryIndex) => (
                <Accordion
                  key={categoryIndex}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleAccordion(categoryIndex.toString() + '_t');
                  }}
                >
                  <AccordionSummary sx={{ border: `1px solid ${themeColors[0]}`, color: `${themeColors[0]}`, margin: '.5rem 0' }}
                    expandIcon={expanded[categoryIndex + '_t'] ? <ExpandLessIcon sx={{ color: `${themeColors[0]}` }} /> : <ExpandMoreIcon sx={{ color: `${themeColors[0]}` }} />}
                  >  {titleIcon[categoryIndex]}
                    <Typography sx={{ lineHeight: '1em', height: '1em', display: 'inline-block', marginTop: '.9rem' }}>{category.category_name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ height: expanded[categoryIndex + '_t'] ? 'auto' : 0, display: expanded[categoryIndex + '_t'] ? 'block' : 'none', overflow: 'hidden', padding: '.5rem 0' }}>
                    {category.help_contents.map((content, contentIndex) => (
                      <Accordion
                        key={contentIndex}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAccordion(categoryIndex.toString() + "_" + contentIndex.toString());
                        }}
                      >
                        <AccordionSummary sx={{ border: `1px solid ${themeColors[0]}`, color: `${themeColors[0]}`, margin: '0 0', padding: '.5rem 1rem' }}
                          expandIcon={expanded[categoryIndex + "_" + contentIndex] ? <ExpandLessIcon sx={{ color: `${themeColors[0]}` }} /> : <ExpandMoreIcon sx={{ color: `${themeColors[0]}` }} />}
                        >
                          <HelpOutlineOutlinedIcon sx={{ margin: '0 .8rem 0 3rem' }} />
                          <Typography>{content.question_text}</Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            height: expanded[categoryIndex + "_" + contentIndex] ? 'auto' : 0,
                            display: expanded[categoryIndex + "_" + contentIndex] ? 'flex' : 'none',
                            overflow: 'hidden',
                            border: `1px solid ${themeColors[3]}`,
                            color: themeColors[3],
                            margin: '0 0 0',
                            padding: '1.2rem 1rem 1.2rem 4rem'
                          }}>
                          <QuestionAnswerOutlinedIcon sx={{ marginRight: '0.8rem' }} />
                          <Typography sx={{ display: 'inline' }}>
                            {content.answer_text.split('<br />').map((line, index) => (
                              <React.Fragment key={index}>
                                {line}
                                <br />
                              </React.Fragment>
                            ))}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </>
        )}
        <Toolbar />
      </Container>
    </>
  );
};

export default HelpPage;
